"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { CookieConsentBanner } from "./CookieConsentBanner";
import { CookieConsentSettingsModal } from "./CookieConsentSettingsModal";
import {
  COOKIE_CONSENT_KEY,
  CookieConsentMethod,
  createPreferences,
  parseConsent,
  toVersion,
} from "./utils/cookie-consent.utils";

export default function CookieConsent() {
  const ecommerce = useEcommerce();
  const t = useTranslations("CookieConsent");
  const pathname = usePathname();
  const [isReady, setIsReady] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const consentVersion = toVersion(ecommerce.cookieConsent?.version);
  const content = ecommerce.cookieConsent;

  useEffect(() => {
    const saved = parseConsent(
      localStorage.getItem(COOKIE_CONSENT_KEY),
      consentVersion
    );
    setIsBannerVisible(!saved);
    setIsReady(true);
  }, [consentVersion]);

  useEffect(() => {
    if (!isSettingsOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSettingsOpen]);

  useEffect(() => {
    setIsSettingsOpen(false);
  }, [pathname]);

  const saveConsent = (method: CookieConsentMethod) => {
    const preferences = createPreferences(method, consentVersion);
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
    setIsSettingsOpen(false);
    setIsBannerVisible(false);
  };

  if (!isReady || !isBannerVisible) return null;

  return (
    <>
      <CookieConsentBanner
        content={content}
        t={t}
        onManage={() => setIsSettingsOpen(true)}
        onAcceptAll={() => saveConsent("all")}
      />
      {isSettingsOpen && (
        <CookieConsentSettingsModal
          content={content}
          t={t}
          onClose={() => setIsSettingsOpen(false)}
          onAcceptNecessary={() => saveConsent("necessary")}
          onAcceptAll={() => saveConsent("all")}
        />
      )}
    </>
  );
}
